import {Box, Stack, Typography} from "@mui/material";
import {ReactNode} from "react";
import {ChatChart} from "./ChatChart";
import {parseChatChartSpec} from "./chatChartParse";

type Block =
  | {type: "code"; text: string}
  | {type: "chart"; spec: NonNullable<ReturnType<typeof parseChatChartSpec>>}
  | {type: "heading"; level: number; text: string}
  | {type: "ul"; items: string[]}
  | {type: "ol"; items: string[]}
  | {type: "table"; header: string[]; rows: string[][]}
  | {type: "paragraph"; text: string};

const chipSx = {
  fontFamily: "monospace",
  color: "inherit",
  bgcolor: "color-mix(in srgb, currentColor 16%, transparent)",
};

// Solo URL http(s) assoluti. Il parser di URL scarta javascript:, data:, mailto:
// e gli schemi offuscati; niente base, così un path relativo non diventa un link.
const safeHttpHref = (value: string): string | null => {
  if (!value || /[\u0000-\u001F\u007F]/.test(value)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return null;
  }
  if (!url.hostname || !/^https?:\/\//i.test(url.href)) {
    return null;
  }
  return url.href;
};

const splitCells = (line: string) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map(cell => cell.trim());

const isTableSeparator = (line: string) =>
  /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());

const parseText = (raw: string): Block[] => {
  const lines = raw.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    const text = paragraph.join("\n").trim();
    if (text) {
      blocks.push({type: "paragraph", text});
    }
    paragraph = [];
  };

  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const ul = /^[-*]\s+(.*)$/.exec(line);
    const ol = /^\d+\.\s+(.*)$/.exec(line);

    if (!line.trim()) {
      flushParagraph();
      index++;
      continue;
    }
    if (heading) {
      flushParagraph();
      blocks.push({
        type: "heading",
        level: heading[1].length,
        text: heading[2],
      });
      index++;
      continue;
    }
    if (
      line.includes("|") &&
      index + 1 < lines.length &&
      isTableSeparator(lines[index + 1])
    ) {
      flushParagraph();
      const header = splitCells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && lines[index].includes("|")) {
        if (isTableSeparator(lines[index])) {
          index++;
          break;
        }
        rows.push(splitCells(lines[index]));
        index++;
      }
      blocks.push({type: "table", header, rows});
      continue;
    }
    if (ul || ol) {
      flushParagraph();
      const ordered = Boolean(ol);
      const items: string[] = [];
      while (index < lines.length) {
        const item = (ordered ? /^\d+\.\s+(.*)$/ : /^[-*]\s+(.*)$/).exec(
          lines[index],
        );
        if (!item) {
          break;
        }
        items.push(item[1]);
        index++;
      }
      blocks.push({type: ordered ? "ol" : "ul", items});
      continue;
    }
    paragraph.push(line.trim());
    index++;
  }
  flushParagraph();
  return blocks;
};

const fenceLanguage = (info: string) => info.trim().split(/\s+/)[0]?.toLowerCase() ?? "";

const parseBlocks = (content: string): Block[] => {
  const blocks: Block[] = [];
  const fence = /```([^\n`]*)\n?([\s\S]*?)```/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = fence.exec(content))) {
    if (match.index > last) {
      blocks.push(...parseText(content.slice(last, match.index)));
    }
    const body = match[2].replace(/\n$/, "");
    if (fenceLanguage(match[1]) === "chart") {
      const spec = parseChatChartSpec(body);
      if (spec) {
        blocks.push({type: "chart", spec});
      } else {
        blocks.push({type: "code", text: body});
      }
    } else {
      blocks.push({type: "code", text: body});
    }
    last = match.index + match[0].length;
  }
  if (last < content.length) {
    blocks.push(...parseText(content.slice(last)));
  }
  return blocks;
};

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  const nodes: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = pattern.exec(text))) {
    const token = match[0];
    const key = `${keyPrefix}-${index}`;
    // ![alt](url) non deve diventare un <img> né un link: il browser non deve
    // richiedere l'URL da solo.
    const image =
      token.startsWith("[") && match.index > 0 && text[match.index - 1] === "!";
    if (match.index > last) {
      const end = image ? match.index - 1 : match.index;
      if (end > last) {
        nodes.push(text.slice(last, end));
      }
    }
    if (token.startsWith("`")) {
      nodes.push(
        <Box
          key={key}
          component="code"
          sx={{
            ...chipSx,
            px: 0.5,
            borderRadius: 0.5,
          }}>
          {token.slice(1, -1)}
        </Box>,
      );
    } else if (token.startsWith("**") || token.startsWith("__")) {
      nodes.push(
        <Box key={key} component="strong">
          {token.slice(2, -2)}
        </Box>,
      );
    } else if (token.startsWith("*")) {
      nodes.push(
        <Box key={key} component="em">
          {token.slice(1, -1)}
        </Box>,
      );
    } else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const label = link?.[1] ?? token;
      const href = image ? null : safeHttpHref(link?.[2]?.trim() ?? "");
      if (href) {
        nodes.push(
          <Box
            key={key}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{color: "inherit", textDecoration: "underline"}}>
            {label}
          </Box>,
        );
      } else {
        nodes.push(<span key={key}>{label}</span>);
      }
    }
    last = match.index + token.length;
    index++;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes;
};

const renderBlock = (block: Block, index: number) => {
  if (block.type === "chart") {
    return (
      <Box key={index} sx={{width: "100%", minWidth: 0}}>
        <ChatChart spec={block.spec} />
      </Box>
    );
  }
  if (block.type === "code") {
    return (
      <Box
        key={index}
        component="pre"
        sx={{
          ...chipSx,
          m: 0,
          p: 1,
          overflow: "auto",
          borderRadius: 1,
          fontSize: "0.85rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
        <Box component="code">{block.text}</Box>
      </Box>
    );
  }
  if (block.type === "heading") {
    return (
      <Typography key={index} fontWeight={600} variant="body1">
        {renderInline(block.text, `h-${index}`)}
      </Typography>
    );
  }
  if (block.type === "table") {
    return (
      <Box key={index} sx={{overflow: "auto"}}>
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            color: "inherit",
            "& th, & td": {
              border: "1px solid",
              borderColor: "color-mix(in srgb, currentColor 45%, transparent)",
              px: 1,
              py: 0.5,
              textAlign: "left",
              verticalAlign: "top",
            },
            "& th": {fontWeight: 600},
          }}>
          <Box component="thead">
            <Box component="tr">
              {block.header.map((cell, cellIndex) => (
                <Box key={cellIndex} component="th">
                  {renderInline(cell, `th-${index}-${cellIndex}`)}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {block.rows.map((row, rowIndex) => (
              <Box key={rowIndex} component="tr">
                {row.map((cell, cellIndex) => (
                  <Box key={cellIndex} component="td">
                    {renderInline(cell, `td-${index}-${rowIndex}-${cellIndex}`)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }
  if (block.type === "ul" || block.type === "ol") {
    const ListTag = block.type === "ol" ? "ol" : "ul";
    return (
      <Box
        key={index}
        component={ListTag}
        sx={{
          m: 0,
          pl: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}>
        {block.items.map((item, itemIndex) => (
          <Box key={itemIndex} component="li">
            {renderInline(item, `li-${index}-${itemIndex}`)}
          </Box>
        ))}
      </Box>
    );
  }
  const lines = block.text.split("\n");
  return (
    <Typography key={index} variant="body1" component="p" sx={{m: 0}}>
      {lines.map((line, lineIndex) => (
        <span key={lineIndex}>
          {lineIndex > 0 && <br />}
          {renderInline(line, `p-${index}-${lineIndex}`)}
        </span>
      ))}
    </Typography>
  );
};

const ChatMarkdown = ({content}: {content: string}) => {
  const blocks = parseBlocks(content);
  if (blocks.length === 0) {
    return null;
  }
  return <Stack gap={0.75}>{blocks.map(renderBlock)}</Stack>;
};

export {ChatMarkdown, parseBlocks};
